import axios from 'axios';
import type { ChatMessage, ChatRequest, ChatResponse, ChatStatusResponse, CuratedState } from '@citiguide/shared';
import apiClient from './api';
import { toChatLocale } from '../utils/chatLocale';

/** Server limits (chatController): 1-8 messages, each 1-1000 chars, last one from the user. */
export const CHAT_MAX_MESSAGES = 8;
export const CHAT_MAX_MESSAGE_CHARS = 1000;
/** The server deadline is 25 s; allow a little slack for the network. */
export const CHAT_TIMEOUT_MS = 30_000;

const STATUS_MODES = ['pro', 'flash', 'chips_only'];

/** The CDN maps a backend 404 to the SPA index.html with a 200, so a body that is not JSON is "unavailable". */
export const isChatStatusBody = (data: unknown): data is ChatStatusResponse => {
  const d = data as Partial<ChatStatusResponse> | null;
  return (
    typeof data === 'object' &&
    data !== null &&
    !Array.isArray(data) &&
    d?.enabled === true &&
    typeof d.mode === 'string' &&
    STATUS_MODES.includes(d.mode) &&
    Array.isArray(d.fields)
  );
};

const isChatResponseBody = (data: unknown): data is ChatResponse => {
  const d = data as Partial<ChatResponse> | null;
  return (
    typeof data === 'object' &&
    data !== null &&
    !Array.isArray(data) &&
    typeof d?.state === 'object' &&
    d.state !== null &&
    typeof d.meta === 'object' &&
    d.meta !== null
  );
};

/** Thrown when POST /api/chat answered 200 with something that is not a chat response (e.g. the SPA HTML). */
export class ChatUnavailableError extends Error {
  constructor() {
    super('chat_unavailable');
    this.name = 'ChatUnavailableError';
  }
}

/**
 * GET /api/chat/status. A 404 (`chat_not_enabled`) -- and, through the production CDN, an HTML 200 --
 * means the tenant has the feature off: null, and the UI shows no chat entry point at all. Any other
 * failure or malformed body is also null (fail closed: no entry point, no error shown).
 * Called once per page load because it shares the per-IP rate limit with turns.
 */
export async function getChatStatus(): Promise<ChatStatusResponse | null> {
  try {
    const response = await apiClient.get<unknown>('/api/chat/status');
    return isChatStatusBody(response.data) ? response.data : null;
  } catch {
    return null;
  }
}

/** A non-chat 200 body (HTML from the CDN fallback) throws ChatUnavailableError: surface chips_only. */
export async function postChatTurn(request: ChatRequest): Promise<ChatResponse> {
  const response = await apiClient.post<unknown>('/api/chat', request, { timeout: CHAT_TIMEOUT_MS });
  if (!isChatResponseBody(response.data)) throw new ChatUnavailableError();
  return response.data;
}

/**
 * Build the request: the last 8 transcript entries, each trimmed and cut to 1000 chars (an assistant
 * reply can be longer than the server's per-message cap, and re-sending it uncut would 400 every
 * following turn), always ending on the new user message. `location` only when the browser permission
 * has been granted (a location exists).
 */
export function buildChatRequest(input: {
  messages: ChatMessage[];
  state: CuratedState;
  uiLanguage: string;
  location?: readonly [number, number] | null;
}): ChatRequest {
  const window_ = input.messages
    .map((m): ChatMessage => ({ role: m.role, content: m.content.trim().slice(0, CHAT_MAX_MESSAGE_CHARS) }))
    .filter((m) => m.content.length > 0)
    .slice(-CHAT_MAX_MESSAGES);
  const request: ChatRequest = {
    locale: toChatLocale(input.uiLanguage),
    messages: window_,
    state: input.state,
  };
  if (input.location) request.location = { lat: input.location[0], lng: input.location[1] };
  return request;
}

export type ChatErrorKind = 'rate_limited' | 'busy' | 'not_enabled' | 'unavailable' | 'failed';

export interface ChatErrorInfo {
  kind: ChatErrorKind;
  /** Seconds the input stays disabled (rate_limited / busy only). */
  cooldownSeconds: number;
}

const DEFAULT_RATE_LIMIT_COOLDOWN_S = 60;
const BUSY_COOLDOWN_S = 5;
const MAX_COOLDOWN_S = 3600;

/** Map an axios error to what the panel does about it. No POST is ever retried automatically. */
export function classifyChatError(error: unknown): ChatErrorInfo {
  if (error instanceof ChatUnavailableError) return { kind: 'unavailable', cooldownSeconds: 0 };
  if (!axios.isAxiosError(error) || !error.response) return { kind: 'failed', cooldownSeconds: 0 };
  const { status, data, headers } = error.response;
  const code = (data as { error?: string } | undefined)?.error;
  if (status === 429 && code === 'busy') return { kind: 'busy', cooldownSeconds: BUSY_COOLDOWN_S };
  if (status === 429) {
    const raw = Number(headers?.['retry-after'] ?? headers?.['ratelimit-reset']);
    const seconds = Number.isFinite(raw) && raw > 0 ? Math.min(Math.ceil(raw), MAX_COOLDOWN_S) : DEFAULT_RATE_LIMIT_COOLDOWN_S;
    return { kind: 'rate_limited', cooldownSeconds: seconds };
  }
  if (status === 404 && code === 'chat_not_enabled') return { kind: 'not_enabled', cooldownSeconds: 0 };
  return { kind: 'failed', cooldownSeconds: 0 };
}
